import { setDesignDataAction } from 'editor/src/store/design/slice';
import resizeDesignToDimensions, { DesignSizeBoundsMm } from 'editor/src/store/design/util/resizeDesignToDimensions';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import getBoxedValue from 'editor/src/util/getBoxedValue';

function isUsableAxis(axis: { minMm: number; maxMm: number } | undefined): boolean {
  return !!axis && axis.minMm > 0 && axis.maxMm >= axis.minMm;
}

function isUsableTrim(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

/**
 * Resize the loaded design to a host-chosen trim (mm), for a custom-format product
 * whose size is picked outside the editor (a Store Link buy box, for instance).
 *
 * A host posts it straight after `design.setDesignData`: the receiver holds it until
 * the design load it was queued behind has finished, so it always resizes a loaded
 * design rather than reading an empty store.
 *
 * The bounds are the product's own limits and are stamped onto designData.dimensions,
 * so a size the host asks for outside them is clamped rather than rejected — the host
 * validates against the same platform rules before sending.
 */
const applyProductDimensionsOperation =
  (widthMm: number, heightMm: number, bounds: DesignSizeBoundsMm | undefined) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const { designData } = getState().design;
    // The whole payload crosses an origin boundary. Unusable limits would be stamped
    // onto the design as its own min/max and a non-numeric trim would propagate NaN
    // through every page, so a malformed message is dropped rather than applied.
    if (!designData || !isUsableTrim(widthMm) || !isUsableTrim(heightMm)) {
      return;
    }
    if (!bounds || !isUsableAxis(bounds.width) || !isUsableAxis(bounds.height)) {
      return;
    }

    const width = getBoxedValue(widthMm, bounds.width.minMm, bounds.width.maxMm);
    const height = getBoxedValue(heightMm, bounds.height.minMm, bounds.height.maxMm);
    const { dimensions } = designData;
    const trimMatches = width === dimensions?.width && height === dimensions?.height;
    const boundsMatch =
      dimensions?.min_width === bounds.width.minMm &&
      dimensions?.max_width === bounds.width.maxMm &&
      dimensions?.min_height === bounds.height.minMm &&
      dimensions?.max_height === bounds.height.maxMm;
    if (trimMatches && boundsMatch) {
      return;
    }

    const resized = resizeDesignToDimensions(designData, width, height, bounds);
    // The resize returns the design untouched when it cannot find a trim to scale from
    // (a structure with no pages). Dispatching that would announce a change that did
    // not happen.
    if (resized === designData) {
      return;
    }

    dispatch(setDesignDataAction(resized));
  };

export default applyProductDimensionsOperation;
